'use client';

import React, { useEffect } from 'react';
import './Modal.scss';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface ModalProps {
  children: React.ReactNode;
  closeOnBackdropClick?: boolean;
  isOpen: boolean;
  onClose: () => void;
  header?: React.ReactNode;
  variant?: 'light' | 'dark';
  className?: string;
}

export function Modal({
  children,
  closeOnBackdropClick,
  isOpen,
  onClose,
  header,
  variant = 'light',
  className,
}: ModalProps) {
  const dialogRef = React.useRef<HTMLDialogElement>(null);

  const handleEscapeKey = React.useCallback(
    (e: KeyboardEvent) => {
      if (e.code === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  const memoizedHandleOpen = React.useCallback(handleOpen, [handleEscapeKey]);

  useEffect(() => {
    if (isOpen) {
      memoizedHandleOpen();
    }
  }, [isOpen, memoizedHandleOpen]);

  function handleOpen() {
    dialogRef.current?.showModal();
    document.addEventListener('keydown', handleEscapeKey);

    return () => document.removeEventListener('keydown', handleEscapeKey);
  }

  return (
    <>
      {isOpen && (
        <dialog ref={dialogRef} className="modal">
          <div
            className={clsx(
              `modal-box overflow-y-auto border border-dark-background ${variant === 'dark' ? 'bg-[#141415]' : 'bg-white'} p-6 md:p-10`,
              className,
            )}
          >
            <form method="dialog">{header ? header : <ModalHeader onClose={onClose} />}</form>
            {children}
          </div>
          {closeOnBackdropClick && (
            <form method="dialog" className="modal-backdrop">
              <button onClick={onClose}>close</button>
            </form>
          )}
        </dialog>
      )}
    </>
  );
}

export function ModalHeader({ onClose }: { onClose: () => void }) {
  return (
    <button className="btn-ghost btn btn-circle btn-sm absolute right-2 top-2 text-gray-4" onClick={onClose}>
      <XMarkIcon className="h-5" />
    </button>
  );
}
